import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { naturalSortKey } from "@/utils/naturalSort";
import type { PortfolioRow, ReportingMode } from "../types";
import { REPORTING_MODE_LABEL } from "../types";

// Zaawansowane filtry portfela ZE: po metadanych projektu (wszystkie pola profilu ZE
// POZA „kolumnami dodatkowymi" — te są per projekt dowolne, więc nie da się z nich
// zrobić wspólnej listy). Filtrowanie liczy się po stronie klienta, tak jak reszta
// filtrów portfela — wiersz z /portfolio/ niesie już wszystkie te pola.

/** Wartość filtru „— brak —" (wiersz z pustym polem). "" = filtr nieaktywny. */
export const META_EMPTY = "__PP_EMPTY__";

type MetaKind = "text" | "select" | "bool" | "date";

export interface MetaFilterDef {
  key: string;
  label: string;
  kind: MetaKind;
  /** Wartość wiersza: string (text/select), boolean (bool), data ISO lub null (date). */
  value: (r: PortfolioRow) => string | boolean | null;
  /** Etykieta pozycji na liście wyboru (domyślnie sama wartość). */
  optionLabel?: (v: string) => string;
}

interface MetaFilterGroup {
  title: string;
  fields: MetaFilterDef[];
}

const text = (v: unknown) => (v == null ? "" : String(v));

export const META_FILTER_GROUPS: MetaFilterGroup[] = [
  {
    title: "Umowa",
    fields: [
      { key: "contract_number", label: "Nr umowy", kind: "text", value: (r) => r.contract_number },
      { key: "st_number", label: "Nr ST", kind: "text", value: (r) => r.st_number },
      { key: "wp_number", label: "Nr WP", kind: "text", value: (r) => r.wp_number },
      { key: "contract_signed_at", label: "Data zawarcia", kind: "date", value: (r) => r.contract_signed_at },
      { key: "contract_deadline", label: "Termin umowy", kind: "date", value: (r) => r.contract_deadline },
      { key: "power_of_attorney", label: "Pełnomocnictwo", kind: "bool", value: (r) => r.power_of_attorney },
    ],
  },
  {
    title: "Kontakt z umowy",
    fields: [
      { key: "contract_contact_name", label: "Osoba", kind: "text", value: (r) => r.contract_contact_name },
      { key: "contract_contact_phone", label: "Telefon", kind: "text", value: (r) => r.contract_contact_phone },
      { key: "contract_contact_email", label: "E-mail", kind: "text", value: (r) => r.contract_contact_email },
    ],
  },
  {
    title: "Kontakt z ST / WP",
    fields: [
      { key: "st_contact_name", label: "Osoba", kind: "text", value: (r) => r.st_contact_name },
      { key: "st_contact_phone", label: "Telefon", kind: "text", value: (r) => r.st_contact_phone },
      { key: "st_contact_email", label: "E-mail", kind: "text", value: (r) => r.st_contact_email },
    ],
  },
  {
    title: "Wniosek o regulację (OMI)",
    fields: [
      { key: "connection_group", label: "Grupa przyłączeniowa", kind: "select", value: (r) => r.connection_group },
      { key: "omi_responsible_name", label: "Osoba odpowiedzialna w OMI", kind: "text", value: (r) => r.omi_responsible_name },
      { key: "td_order_date", label: "Data zlecenia od TD", kind: "date", value: (r) => r.td_order_date },
      { key: "psp_zadania", label: "PSP zadania (TAURON)", kind: "text", value: (r) => r.psp_zadania },
    ],
  },
  {
    title: "Profil projektu",
    fields: [
      { key: "operator", label: "Operator", kind: "select", value: (r) => r.operator_display },
      {
        key: "srid",
        label: "Strefa EPSG",
        kind: "select",
        value: (r) => text(r.srid),
        optionLabel: (v) => `EPSG:${v}`,
      },
      { key: "designer_name", label: "Projektant", kind: "select", value: (r) => text(r.designer_name) },
      { key: "lead_designer_name", label: "Projektant uprawniony", kind: "select", value: (r) => text(r.lead_designer_name) },
      { key: "ze_coordinator_name", label: "Opracowujący (ELEKTIM)", kind: "select", value: (r) => text(r.ze_coordinator_name) },
      { key: "source_tender_title", label: "Przetarg ZE (źródło)", kind: "select", value: (r) => text(r.source_tender_title) },
      { key: "process_template_name", label: "Szablon procesu", kind: "select", value: (r) => text(r.process_template_name) },
      {
        key: "kpi_interval_days",
        label: "Interwał KPI",
        kind: "select",
        value: (r) => text(r.kpi_interval_days),
        optionLabel: (v) => `${v} dni`,
      },
      { key: "notes", label: "Uwagi", kind: "text", value: (r) => r.notes },
    ],
  },
  {
    title: "Raporty okresowe do ZE",
    fields: [
      {
        key: "reporting_mode",
        label: "Raportowanie",
        kind: "select",
        value: (r) => r.reporting_mode,
        optionLabel: (v) => REPORTING_MODE_LABEL[v as ReportingMode] ?? v,
      },
      {
        key: "reporting_interval_months",
        label: "Interwał raportów",
        kind: "select",
        value: (r) => text(r.reporting_interval_months),
        optionLabel: (v) => `${v} mies.`,
      },
      {
        key: "reporting_start_override",
        label: "Start raportowania (nadpisanie)",
        kind: "date",
        value: (r) => r.reporting_start_override,
      },
    ],
  },
];

export const META_FILTER_DEFS: MetaFilterDef[] = META_FILTER_GROUPS.flatMap((g) => g.fields);

/** Klucze stanu filtru „od"/„do" dla pola datowego. */
const dateKeys = (key: string) => [`${key}__from`, `${key}__to`] as const;

/** Czy w wartościach filtru to pole jest ustawione (data = którakolwiek granica). */
function isSet(def: MetaFilterDef, values: Record<string, string>): boolean {
  if (def.kind === "date") return dateKeys(def.key).some((k) => !!values[k]);
  return !!values[def.key];
}

/** Ile filtrów metadanych jest aktywnych (zakres daty liczy się jako jeden). */
export function countMetaFilters(values: Record<string, string>): number {
  return META_FILTER_DEFS.filter((d) => isSet(d, values)).length;
}

/** Tekst wiersza do prostego pola „Szukaj": wartości wszystkich pól metadanych
 *  (tekst/wybór/data; pola tak/nie pominięte — "tak" trafiałoby wszędzie).
 *  Dokłada też etykiety widoczne dla użytkownika (np. "EPSG:2177", "12 dni"). */
export function metaSearchText(r: PortfolioRow): string {
  const parts: string[] = [];
  for (const def of META_FILTER_DEFS) {
    if (def.kind === "bool") continue;
    const v = def.value(r);
    if (v == null || v === "") continue;
    const s = String(v);
    parts.push(s);
    if (def.optionLabel) parts.push(def.optionLabel(s));
  }
  return parts.join(" ");
}

/** Czy wiersz przechodzi filtry metadanych. `skipKey` pomija JEDNO pole — dzięki temu
 *  lista wyboru danego filtru pokazuje wartości dopuszczone przez POZOSTAŁE filtry. */
export function matchesMetaFilters(
  r: PortfolioRow,
  values: Record<string, string>,
  skipKey?: string,
): boolean {
  for (const def of META_FILTER_DEFS) {
    if (def.key === skipKey || !isSet(def, values)) continue;
    if (def.kind === "date") {
      const [fromKey, toKey] = dateKeys(def.key);
      const from = values[fromKey] || "";
      const to = values[toKey] || "";
      const v = def.value(r) as string | null;
      // Brak daty nie mieści się w żadnym zakresie.
      if (!v) return false;
      if (from && v < from) return false;
      if (to && v > to) return false;
      continue;
    }
    const sel = values[def.key];
    if (def.kind === "bool") {
      if ((sel === "yes") !== (def.value(r) === true)) return false;
      continue;
    }
    const v = text(def.value(r));
    if (def.kind === "select") {
      if (sel === META_EMPTY ? v !== "" : v !== sel) return false;
      continue;
    }
    if (!v.toLowerCase().includes(sel.trim().toLowerCase())) return false;
  }
  return true;
}

function FilterField({
  def,
  value,
  onChange,
  options,
}: {
  def: MetaFilterDef;
  /** Dla pól datowych nieużywane — komponent czyta obie granice z `values`. */
  value: string;
  onChange: (key: string, v: string) => void;
  options: string[];
}) {
  if (def.kind === "select") {
    // "" wśród wartości = są wiersze z pustym polem → pozycja „— brak —".
    const hasEmpty = options.includes("");
    const filled = options
      .filter(Boolean)
      .sort((a, b) => naturalSortKey(a).localeCompare(naturalSortKey(b), "pl"));
    return (
      <select
        className="input-base text-xs py-1"
        value={value}
        onChange={(e) => onChange(def.key, e.target.value)}
      >
        <option value="">— wszystkie —</option>
        {hasEmpty && <option value={META_EMPTY}>— brak —</option>}
        {filled.map((o) => (
          <option key={o} value={o}>{def.optionLabel ? def.optionLabel(o) : o}</option>
        ))}
      </select>
    );
  }
  if (def.kind === "bool") {
    return (
      <select
        className="input-base text-xs py-1"
        value={value}
        onChange={(e) => onChange(def.key, e.target.value)}
      >
        <option value="">— wszystkie —</option>
        <option value="yes">Tak</option>
        <option value="no">Nie</option>
      </select>
    );
  }
  return (
    <input
      className="input-base text-xs py-1"
      placeholder="zawiera…"
      value={value}
      onChange={(e) => onChange(def.key, e.target.value)}
    />
  );
}

export function PortfolioMetadataFilters({
  values,
  onChange,
  onClear,
  optionsFor,
}: {
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onClear: () => void;
  /** Wartości występujące w wierszach dopuszczonych przez pozostałe filtry ("" = puste pole). */
  optionsFor: (def: MetaFilterDef) => string[];
}) {
  const active = countMetaFilters(values);

  return (
    <div className="card p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="section-label normal-case tracking-normal">
          Filtry po metadanych projektu. Kolumny dodatkowe (własne, per projekt) nie są tu
          uwzględnione — filtruj po nich w raportach.
        </p>
        {active > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            Wyczyść zaawansowane
          </Button>
        )}
      </div>
      {META_FILTER_GROUPS.map((g) => (
        <div key={g.title} className="space-y-2">
          <h3 className="font-mono text-[0.65rem] font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">
            {g.title}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {g.fields.map((def) => (
              // Zakres daty = dwa pola, więc dostaje podwójną szerokość siatki.
              <label key={def.key} className={def.kind === "date" ? "block sm:col-span-2" : "block"}>
                <span className="section-label">{def.label}</span>
                <div className="mt-1">
                  {def.kind === "date" ? (
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 min-w-0">
                        <DatePicker
                          className="py-1"
                          placeholder="od…"
                          value={values[`${def.key}__from`] ?? ""}
                          onChange={(v) => onChange(`${def.key}__from`, v)}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <DatePicker
                          className="py-1"
                          placeholder="do…"
                          value={values[`${def.key}__to`] ?? ""}
                          onChange={(v) => onChange(`${def.key}__to`, v)}
                        />
                      </div>
                    </div>
                  ) : (
                    <FilterField
                      def={def}
                      value={values[def.key] ?? ""}
                      onChange={onChange}
                      options={optionsFor(def)}
                    />
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
